import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import {
  buildRequestFailureCacheKey,
  clearRequestFailure,
} from '../../shared/api/requestFailureCache.js';
import { showToast } from '../../shared/ui/toastStore.js';
import type { EmailLocalePreferenceDTO } from './accountTypes.js';
import {
  resolveEmailUiLocale,
  type EmailLocaleCode,
} from './emailLocalePreference.js';
import {
  isDefaultEmailPreference,
  needsEmailUiLocaleSync,
  nextCoalescedEmailLocale,
} from './emailLocaleSync.js';
import {
  registerEmailLocaleSyncRetry,
  setEmailLocaleSyncStatus,
} from './emailLocaleSyncStatus.js';
import { isAccountQueryEnabled } from './accountApi.js';

export { getLastEmailLocaleSyncError } from './emailLocaleSyncStatus.js';

/** Quiet remount / login race recovery — avoid alarming toast for background sync. */
export const EMAIL_LOCALE_MOUNT_SOFT_RETRY_MS = 2_000;

const EMAIL_LOCALE_PATH = '/customer-account/preferences/email-locale';

export function clearLastEmailLocaleSyncError(): void {
  setEmailLocaleSyncStatus('idle');
}

function clearEmailLocaleGetFailure(tenantCode: string): void {
  clearRequestFailure(
    buildRequestFailureCacheKey(['GET', tenantCode, EMAIL_LOCALE_PATH]),
  );
}

async function requestWithOneRetry<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch {
    return await run();
  }
}

/**
 * While email preference is Default (follow UI), keep server `uiLocale` aligned
 * with i18n so transactional emails match the live UI language without Account → Save.
 */
export function useSyncEmailLocaleOnLanguageChange(): void {
  const { t, i18n } = useTranslation('customer');
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const customerId = session?.customerId;
  const syncEnabled = isAccountQueryEnabled(session, tenantCode);
  const syncingRef = useRef(false);
  const pendingLocaleRef = useRef<EmailLocaleCode | null>(null);
  const toastShownForErrorRef = useRef(false);
  /** Only user language switches / Account Retry should surface a danger toast. */
  const toastOnFailureRef = useRef(false);
  const mountSoftRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountSoftRetryUsedRef = useRef(false);

  useEffect(() => {
    if (!syncEnabled || customerId === undefined) {
      registerEmailLocaleSyncRetry(null);
      return;
    }

    mountSoftRetryUsedRef.current = false;

    const clearMountSoftRetry = (): void => {
      if (mountSoftRetryTimerRef.current !== null) {
        clearTimeout(mountSoftRetryTimerRef.current);
        mountSoftRetryTimerRef.current = null;
      }
    };

    const scheduleQuietMountRetry = (): void => {
      if (
        toastOnFailureRef.current ||
        mountSoftRetryUsedRef.current ||
        mountSoftRetryTimerRef.current !== null
      ) {
        return;
      }
      mountSoftRetryUsedRef.current = true;
      mountSoftRetryTimerRef.current = setTimeout(() => {
        mountSoftRetryTimerRef.current = null;
        toastOnFailureRef.current = false;
        clearEmailLocaleGetFailure(tenantCode);
        enqueueLocale(resolveEmailUiLocale(i18n.resolvedLanguage ?? i18n.language));
      }, EMAIL_LOCALE_MOUNT_SOFT_RETRY_MS);
    };

    const runSync = async (uiLocale: EmailLocaleCode): Promise<void> => {
      setEmailLocaleSyncStatus('syncing');
      try {
        const cached = queryClient.getQueryData<EmailLocalePreferenceDTO>(
          customerQueryKeys.accountEmailLocale(tenantCode, customerId)
        );
        let pref = cached;
        if (pref === undefined) {
          pref = await requestWithOneRetry(() =>
            api.request<EmailLocalePreferenceDTO>(EMAIL_LOCALE_PATH)
          );
          queryClient.setQueryData(
            customerQueryKeys.accountEmailLocale(tenantCode, customerId),
            pref
          );
        }
        if (!isDefaultEmailPreference(pref)) {
          setEmailLocaleSyncStatus('idle');
          toastShownForErrorRef.current = false;
          return;
        }
        if (!needsEmailUiLocaleSync(pref?.uiLocale, uiLocale)) {
          setEmailLocaleSyncStatus('idle');
          toastShownForErrorRef.current = false;
          return;
        }
        const updated = await requestWithOneRetry(() =>
          api.request<EmailLocalePreferenceDTO>(EMAIL_LOCALE_PATH, {
            method: 'PUT',
            body: { mode: 'default', uiLocale },
          })
        );
        queryClient.setQueryData(
          customerQueryKeys.accountEmailLocale(tenantCode, customerId),
          updated
        );
        setEmailLocaleSyncStatus('idle');
        toastShownForErrorRef.current = false;
      } catch (err) {
        setEmailLocaleSyncStatus('error', err);
        if (toastOnFailureRef.current && !toastShownForErrorRef.current) {
          toastShownForErrorRef.current = true;
          showToast({
            message: t('account.settings.emailLocaleSyncFailed'),
            variant: 'danger',
            durationMs: 6000,
          });
        }
        scheduleQuietMountRetry();
      }
    };

    const enqueueLocale = (uiLocale: EmailLocaleCode): void => {
      pendingLocaleRef.current = nextCoalescedEmailLocale(
        pendingLocaleRef.current,
        uiLocale
      );
      if (syncingRef.current) {
        return;
      }
      syncingRef.current = true;
      void (async () => {
        try {
          while (pendingLocaleRef.current !== null) {
            const current = pendingLocaleRef.current;
            pendingLocaleRef.current = null;
            await runSync(current);
          }
        } finally {
          syncingRef.current = false;
          if (pendingLocaleRef.current !== null) {
            enqueueLocale(pendingLocaleRef.current);
          }
        }
      })();
    };

    const syncIfDefault = (rawLanguage: string): void => {
      enqueueLocale(resolveEmailUiLocale(rawLanguage));
    };

    registerEmailLocaleSyncRetry(() => {
      clearMountSoftRetry();
      toastShownForErrorRef.current = false;
      toastOnFailureRef.current = true;
      clearEmailLocaleGetFailure(tenantCode);
      syncIfDefault(i18n.resolvedLanguage ?? i18n.language);
    });

    toastOnFailureRef.current = false;
    syncIfDefault(i18n.resolvedLanguage ?? i18n.language);

    const onLanguageChanged = (lng: string): void => {
      clearMountSoftRetry();
      toastShownForErrorRef.current = false;
      toastOnFailureRef.current = true;
      clearEmailLocaleGetFailure(tenantCode);
      syncIfDefault(lng);
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      clearMountSoftRetry();
      i18n.off('languageChanged', onLanguageChanged);
      registerEmailLocaleSyncRetry(null);
    };
  }, [api, customerId, syncEnabled, i18n, queryClient, t, tenantCode]);
}
